import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/** Mobile Money withdrawals (artists' sales money and affiliation commissions). One still
 * "in progress" can be settled by hand after checking the MyPVit dashboard: paid, or
 * failed (the money then goes back to the person's balance). */

const STATUS = { PENDING: "En cours", PROCESSING: "En cours", COMPLETED: "Payé", FAILED: "Échoué", CANCELLED: "Annulé" };
const nf = (n) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export default function PayoutsAdmin() {
  const { user } = useAuth();
  // marking a withdrawal paid / failed moves money: owner, or someone the owner gave "payouts" to
  const canSettle = user?.role === "admin" || (user?.permissions || []).includes("payouts");
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/payouts").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const resolve = async (p, completed) => {
    const reason = window.prompt(completed
      ? `Confirmer que ${nf(p.payout_amount)} ${p.payout_currency} ont bien été reçus par ${p.user_name} (vérifié sur MyPVit) ?\nNote (obligatoire) :`
      : `Marquer ce retrait comme ÉCHOUÉ ? Le montant revient dans le solde de ${p.user_name}.\nRaison (obligatoire) :`);
    if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error("Écris au moins 5 caractères."); return; }
    try {
      await api.post(`/admin/payouts/${p.id}/resolve`, { completed, reason: reason.trim() });
      toast.success(completed ? "Retrait marqué payé." : "Retrait marqué échoué, montant rendu.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
    <PaymentIssues canSettle={canSettle} />
    <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid="admin-payouts">
      <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3 flex items-center justify-between">
        Retraits Mobile Money <span className="text-[10px] text-zinc-500">{rows?.length ?? ""}</span>
      </h3>
      {rows === null ? <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /> : rows.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-6">Aucun retrait</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 uppercase tracking-wider text-[10px] font-black">
              {["Date", "Compte", "Type", "Montant", "Vers", "Statut", ""].map((h) => <th key={h} className="text-left py-2 pr-4">{h}</th>)}
            </tr></thead>
            <tbody>{rows.map((p) => {
              const open = p.status === "PENDING" || p.status === "PROCESSING";
              return (
                <tr key={p.id} className="border-t border-white/5" data-testid={`payout-row-${p.id}`}>
                  <td className="py-2 pr-4 text-zinc-400">{new Date(p.requested_at).toLocaleString("fr-FR")}</td>
                  <td className="py-2 pr-4 text-white">{p.user_name}</td>
                  <td className="py-2 pr-4 text-zinc-300">{p.ledger === "commission" ? "Commissions" : "Ventes"}</td>
                  <td className="py-2 pr-4 text-white tabular-nums">{nf(p.payout_amount)} {p.payout_currency}</td>
                  <td className="py-2 pr-4 text-zinc-400">{p.destination?.method} {p.destination?.msisdn}<div className="text-zinc-500">{p.destination?.name}</div></td>
                  <td className={`py-2 pr-4 font-bold ${p.status === "COMPLETED" ? "text-emerald-400" : p.status === "FAILED" ? "text-red-400" : "text-amber-400"}`}>
                    {STATUS[p.status] || p.status}
                    {p.failure_reason && <div className="text-zinc-500 font-normal">{p.failure_reason}</div>}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {open && canSettle && (
                      <>
                        <button onClick={() => resolve(p, true)} className="text-emerald-400 underline mr-3" data-testid={`payout-paid-${p.id}`}>Payé</button>
                        <button onClick={() => resolve(p, false)} className="text-red-400 underline" data-testid={`payout-failed-${p.id}`}>Échoué</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </section>
    </>
  );
}

const ISSUE = { item_missing: "Chanson ou projet supprimé pendant le paiement", amount_low: "Montant encaissé inférieur au prix", currency_mismatch: "Mauvaise devise" };

/** Payments MyPVit took but that could not be delivered as is: each waits here until the
 * owner refunds the buyer by hand (on MyPVit) or delivers the purchase anyway. */
function PaymentIssues({ canSettle }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/payments/issues").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || rows.length === 0) return null;
  const settle = async (p, action) => {
    const note = window.prompt(action === "grant"
      ? `Donner quand même l'achat à ${p.buyer_name} ? (seulement si la chanson existe encore)\nNote (obligatoire) :`
      : `Confirmer que ${nf(p.amount)} ${p.currency} ont été REMBOURSÉS à ${p.buyer_name} (${p.payer_msisdn || "numéro inconnu"}) sur MyPVit ?\nNote (obligatoire) :`);
    if (!note || note.trim().length < 5) { if (note !== null) toast.error("Écris au moins 5 caractères."); return; }
    try {
      await api.post(`/admin/payments/${p.id}/settle`, { action, note: note.trim() });
      toast.success(action === "grant" ? "Achat donné à l'acheteur." : "Remboursement enregistré.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const open = rows.filter((p) => p.status === "ISSUE");
  return (
    <section className="rounded-2xl bg-amber-500/5 border border-amber-500/20 p-5" data-testid="admin-payment-issues">
      <h3 className="text-sm font-black uppercase tracking-wider text-amber-300 mb-1">Paiements à régulariser <span className="text-[10px] text-zinc-500">{open.length} en attente</span></h3>
      <p className="text-xs text-zinc-400 mb-3">L'acheteur a payé MyPVit mais l'achat n'a pas pu être livré tel quel. Rembourse-le sur MyPVit puis marque-le « Remboursé », ou donne-lui l'achat.</p>
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} className="rounded-xl bg-white/5 px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-zinc-400">{new Date(p.created_at).toLocaleString("fr-FR")}</span>
            <span className="text-white font-bold">{p.buyer_name}</span>
            <span className="text-zinc-300">« {p.title} » · {nf(p.amount)} {p.currency} · {p.payer_msisdn || "—"}</span>
            <span className="text-amber-200">{ISSUE[p.issue] || p.issue}</span>
            {p.status === "ISSUE" ? (canSettle && (
              <span className="ml-auto whitespace-nowrap">
                <button onClick={() => settle(p, "refunded")} className="text-emerald-400 underline mr-3">Remboursé</button>
                <button onClick={() => settle(p, "grant")} className="text-[#00F2FE] underline">Donner l'achat</button>
              </span>
            )) : <span className="ml-auto text-zinc-500">Réglé : {p.status === "REFUNDED" ? "remboursé" : p.status} — {p.issue_settled}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
